import React, { useEffect } from 'react';
import PropTypes from 'prop-types';
import { useHistory } from 'react-router-dom';
import { useSuspense } from '@rest-hooks/react';

import ProfileResource from 'resources/Profile/ProfileResource';

import AuthModal from 'components/authForms/AuthModal';
import RedeemModal from 'components/RedeemModal';
import CheckoutModal from 'components/CheckoutModal';
import CartDrawer from 'components/Cart/CartDrawer';
import CartCheckoutModal from 'components/Cart/CartCheckoutModal';

import Card from 'ui/Card';
import Button from 'ui/Button';

import Header from './Header';
import Footer from './Footer';

import { usePreActionController } from 'utils/usePreActionController';
import { CartProvider, useCart } from 'utils/CartContext';

// Keeps cart items' product snapshots fresh with the latest ticket data so
// restored carts don't render stale supply/redeem counters.
const CartProductSyncer = ({ products }) => {
  const { syncProducts } = useCart();
  useEffect(() => {
    if (products) syncProducts(products);
  }, [syncProducts, products]);
  return null;
};

CartProductSyncer.propTypes = {
  products: PropTypes.array,
};

import ArrowBackIcon from '@mui/icons-material/ArrowBack';

import { t } from "i18n/index";

const SingleCardContent = ({event, organizationSlug, organizationTags, ticketSlug, cartMode}) => {

  const { title, description, start_date, end_date, location, secret_location, image_url, tickets, event_products, show_view_all, slug, single, sessions, tag_ids, requires_approval } = event;

  const history = useHistory();
  const handleRedirect = () => {
    history.push(`/${organizationSlug}/events`);
  };

  const { show_application_fee} = useSuspense(ProfileResource.detail(), {id: organizationSlug});

  const {
    checkoutModals,
    checkoutVars,
    currentAction,
    modals,
    onAction,
    onCartCheckout,
    onCartCheckoutClose,
    onAuthModalOpen,
    onCheckoutModalsChange,
    onCheckoutVarsChange,
    onClose,
    onProductChange,
    onSuccessfulAuth,
  } = usePreActionController(organizationSlug, null, slug);

  return (
    <div className='events-content'>
      <Card
          fullwidth
          header={
            <Header
              image_url={image_url}
              title={title}
              start_date={start_date}
              end_date={end_date}
              location={location}
              tickets={tickets}
              eventProducts={event_products}
              onAction={onAction}
              showApplicationFee={show_application_fee}
              single={single}
              sessions={sessions}
              requires_approval={requires_approval}
              ticketSlug={ticketSlug}
              cartMode={cartMode}
              />}

          footer={
            <Footer
              location={location}
              secret_location={secret_location}
              organizationTags={organizationTags}
              tag_ids={tag_ids}
              />}

          description={description} />

      {show_view_all &&
        <Button
          secondary
          icon={ArrowBackIcon}
          minwidth
          onClick={handleRedirect}>
            {t('literal:viewAll')}
          </Button>
      }

      {cartMode && (
        <>
          <CartProductSyncer products={[...(tickets || []), ...(event_products || [])]} />
          <CartDrawer onCheckout={onCartCheckout} />
        </>
      )}

      {modals.authModal &&
        <AuthModal
          open={ modals.authModal }
          done={ onSuccessfulAuth }
          onClose={ onClose }
          organizationSlug={ organizationSlug }
          allowPartialSignIn={ true }
          context={ 'event' } />}

      {/* currentAction guards: the async post-auth flow can reopen these modals
          from a stale closure after onClose already nulled currentAction
          (Sentry MEMBRZ-WEB-N) */}
      {modals.redeemModal && currentAction &&
        <RedeemModal
          product={currentAction.product}
          onClose={onClose}
          organizationSlug={organizationSlug}
          eventTitle={ title }
          /> }

      {modals.checkoutModal && currentAction &&
        <CheckoutModal
          checkoutModals={checkoutModals}
          checkoutVars={checkoutVars}
          product={currentAction.product}
          onAuthModalOpen={onAuthModalOpen}
          onCheckoutModalsChange={onCheckoutModalsChange}
          onCheckoutVarsChange={onCheckoutVarsChange}
          onClose={onClose}
          onProductChange={onProductChange}
          isCart={cartMode}
        />}

      {cartMode && modals.cartCheckoutModal && (
        <CartCheckoutModal
          onClose={onCartCheckoutClose}
        />
      )}
    </div>
  );
};

SingleCardContent.propTypes = {
  event: PropTypes.object,
  organizationSlug: PropTypes.string,
  organizationTags: PropTypes.array,
  ticketSlug: PropTypes.string,
  cartMode: PropTypes.bool,
};

const SingleCard = ({event, organizationSlug, organizationTags, ticketSlug}) => {

  const { has_cart } = useSuspense(ProfileResource.detail(), {id: organizationSlug});
  const cartMode = !!has_cart;

  if (cartMode) {
    return (
      <CartProvider>
        <SingleCardContent
          event={event}
          organizationSlug={organizationSlug}
          organizationTags={organizationTags}
          ticketSlug={ticketSlug}
          cartMode={cartMode}
        />
      </CartProvider>
    );
  }

  return (
    <SingleCardContent
      event={event}
      organizationSlug={organizationSlug}
      organizationTags={organizationTags}
      ticketSlug={ticketSlug}
      cartMode={false}
    />
  );
};

SingleCard.propTypes = {
  event: PropTypes.array,
  organizationSlug: PropTypes.string,
  organizationTags: PropTypes.array,
  ticketSlug: PropTypes.string
};

export default SingleCard;
